import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { env } from 'env';
import { toast } from 'react-toastify';
import messages from 'shared/constants/messages';
import { HttpStatusCode } from 'shared/enums/http-status-code';
import store from 'store';
import { logout, refreshToken } from 'store/actions/auth-actions';
import { AuthActionType } from 'store/actionTypes/auth-action-type';
import { IApplicationState } from 'store/state/app-state';
import { getBaseURL } from 'utils/common-functions';

declare module 'axios' {
    export interface AxiosRequestConfig {
        showLoader: boolean;
        isBackgroundService?: boolean;
    }
}

export function showLoader() {
    const loaderDiv = document.getElementById('loader');

    if (loaderDiv) {
        loaderDiv.style.display = 'flex';
        loaderDiv.style.visibility = 'visible';
    }
}

export function hideLoader() {
    const loaderDiv = document.getElementById('loader');

    if (loaderDiv) {
        loaderDiv.style.display = 'none';
        loaderDiv.style.visibility = 'hidden';
    }
}

axios.interceptors.request.use(
    (config: AxiosRequestConfig) => {
        const storeData: IApplicationState = store.getState();
        if (storeData?.GeneralData?.isServiceDown) {
            return Promise.reject({
                message: 'Service Down',
            });
        }

        if (config.url) {
            config.url = config.url.startsWith('http') ? config.url : getBaseURL(env.REACT_APP_STORE_URL) + config.url;
        }
        config.headers = {
            ...config.headers,
            Authorization: `bearer ${storeData?.UserData?.authConfigs?.accessToken}`,
            language: process.env.REACT_APP_REGION_LANGUAGE,
            timezoneId: storeData?.UserData?.timezoneId,
            'Cache-Control': 'no-cache',
            Pragma: 'no-cache',
            Expires: '0',
        };

        if (!config?.isBackgroundService) {
            showLoader();
        }

        return config;
    },
    (error: AxiosError) => {
        return Promise.reject(error);
    },
);

let isRefreshing = false;
let failedQueue: Array<(token: string) => void> = [];

axios.interceptors.response.use(
    (response: AxiosResponse) => {
        if (!response?.config?.isBackgroundService) hideLoader();
        if (
            response?.data?.statusCode === HttpStatusCode.Error &&
            response?.data?.errorCode !== HttpStatusCode.InValidAuthToken
        ) {
            toast.error(response?.data?.message);
        }
        return response;
    },
    async (error: AxiosError) => {
        const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };

        if (!error.response) {
            if (!originalRequest?.isBackgroundService) hideLoader();
            store.dispatch({ type: AuthActionType.SET_SERVICE_DOWN, payload: true });

            return Promise.resolve({
                data: null,
                status: 503,
                statusText: 'Service Unavailable',
                headers: {},
                config: originalRequest,
            } as AxiosResponse);
        }

        if (error.response?.status === HttpStatusCode.InValidAuthToken && !originalRequest._retry) {
            originalRequest._retry = true;

            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    failedQueue.push((token: string) => {
                        if (token) {
                            originalRequest.headers['Authorization'] = `bearer ${token}`;
                            resolve(axios(originalRequest));
                        } else {
                            reject(error);
                        }
                    });
                });
            }

            isRefreshing = true;

            try {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const newToken = await store.dispatch<any>(refreshToken());
                isRefreshing = false;
                if (newToken) {
                    failedQueue.forEach((callback) => callback(newToken));
                    failedQueue = [];

                    originalRequest.headers['Authorization'] = `bearer ${newToken}`;
                    return axios(originalRequest);
                } else {
                    failedQueue = [];
                    hideLoader();
                    store.dispatch({ type: AuthActionType.PATIENT_PATIENT_LOGOUT_REQUEST });
                    return Promise.resolve({
                        data: null,
                        status: 401,
                        statusText: 'Unauthorized',
                        headers: {},
                        config: originalRequest,
                    } as AxiosResponse);
                }
            } catch (err) {
                isRefreshing = false;
                failedQueue = [];
                hideLoader();
                store.dispatch({ type: AuthActionType.PATIENT_PATIENT_LOGOUT_REQUEST });
                return Promise.resolve({
                    data: null,
                    status: 401,
                    statusText: 'Unauthorized',
                    headers: {},
                    config: originalRequest,
                } as AxiosResponse);
            }
        }

        switch (error.response?.status) {
            case HttpStatusCode.BadRequest:
            case HttpStatusCode.ConflictError:
            case HttpStatusCode.InternalServerError:
                toast.error(messages.InternalServerError);
                break;
            case HttpStatusCode.Unauthorized:
                logout();
                hideLoader();
                store.dispatch({ type: AuthActionType.PATIENT_PATIENT_LOGOUT_REQUEST });
                return;
        }

        if (!originalRequest?.isBackgroundService) hideLoader();
        return Promise.resolve({
            data: null,
            status: 503,
            statusText: 'Service Unavailable',
            headers: {},
            config: originalRequest,
        } as AxiosResponse);
    },
);

export default {
    get: axios.get,
    post: axios.post,
    put: axios.put,
    delete: axios.delete,
    patch: axios.patch,
};
